import type { CertificateTemplateOption } from '~/gql/certificates/certificates'
import { useMutation, useQuery } from '@apollo/client/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  GQL_CERTIFICATE_TEMPLATES,
  GQL_CREATE_CERTIFICATE_TEMPLATE,
  GQL_DELETE_CERTIFICATE_TEMPLATE,
  GQL_DUPLICATE_CERTIFICATE_TEMPLATE,
  GQL_SET_DEFAULT_CERTIFICATE_TEMPLATE,
} from '~/gql/certificates/certificates'

/**
 * Les modèles d'attestation de l'organisation (#162) et celui en cours
 * d'édition : le défaut tant que l'admin n'en a pas choisi un autre, et le
 * modèle tout juste créé ou dupliqué, pour l'éditer aussitôt.
 */
export function useCertificateTemplates() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useQuery(GQL_CERTIFICATE_TEMPLATES)
  const templates: CertificateTemplateOption[] = data?.certificateTemplates ?? []
  const [chosenId, setChosenId] = useState<string | null>(null)
  // Un modèle supprimé, ou pas encore choisi : retour au premier, le défaut.
  const selected = templates.find(template => template.id === chosenId) ?? templates[0] ?? null

  const [createTemplate] = useMutation(GQL_CREATE_CERTIFICATE_TEMPLATE)
  const [duplicateTemplate] = useMutation(GQL_DUPLICATE_CERTIFICATE_TEMPLATE)
  const [setDefaultTemplate] = useMutation(GQL_SET_DEFAULT_CERTIFICATE_TEMPLATE)
  const [deleteTemplate] = useMutation(GQL_DELETE_CERTIFICATE_TEMPLATE)

  /** `true` si l'action a abouti : un formulaire de saisie ne se ferme qu'alors. */
  const run = async (action: () => Promise<string | null>, successKey: string): Promise<boolean> => {
    try {
      const id = await action()
      await refetch()
      if (id) setChosenId(id)
      toast.success(t(successKey))
      return true
    } catch {
      toast.error(t('certificates.templates.error'))
      return false
    }
  }

  return {
    templates,
    selected,
    loading,
    error,
    refetch,
    select: (id: string) => setChosenId(id),
    create: (name: string) => run(async () => {
      const { data: created } = await createTemplate({ variables: { name } })
      return created?.createCertificateTemplate.id ?? null
    }, 'certificates.templates.created'),
    duplicate: (id: string, name: string) => run(async () => {
      const { data: copy } = await duplicateTemplate({ variables: { id, name } })
      return copy?.duplicateCertificateTemplate.id ?? null
    }, 'certificates.templates.duplicated'),
    makeDefault: (id: string) => run(async () => {
      await setDefaultTemplate({ variables: { id } })
      return id
    }, 'certificates.templates.defaultSet'),
    remove: (id: string) => run(async () => {
      await deleteTemplate({ variables: { id } })
      return null
    }, 'certificates.templates.deleted'),
  }
}
